import { Device as DevicePlugin } from '@capacitor/device';
import { Preferences } from '@capacitor/preferences';
import { BehaviorSubject, distinctUntilChanged, filter, shareReplay, tap } from 'rxjs';

const deviceSubject = new BehaviorSubject<Device>(null);

export const GLOBAL_API_REQUEST_HEADERS = {
    device: ''
};

export interface Device {
    id: string;
    friendlyName: string;
}

export const DEVICE$ = deviceSubject.pipe(
    filter((d) => !!d),
    distinctUntilChanged(),
    tap((device) => {
        // It's a bit weird putting this in a tap rather than in the subscribe,
        // But I want to make sure the header is assigned before any other
        // subscriptions trigger for this observable
        GLOBAL_API_REQUEST_HEADERS.device = JSON.stringify(device);
    }),
    shareReplay(1)
);
DEVICE$.subscribe();

console.log('getting device');

DevicePlugin.getInfo()
    .then(async (deviceInfo) => {
        console.log('device: ', deviceInfo);
        let device = await getDeviceFromLocalStorage();

        if (!device) {
            const friendlyName = deviceInfo.name || getFriendlyNameFromUserAgent() || 'Unknown Device';
            const id = (await DevicePlugin.getId().catch(() => ({ identifier: generateFallbackId() }))).identifier;

            device = { id, friendlyName };
            saveDeviceToLocalStorage(device);
        }
        deviceSubject.next(device);
    })
    .catch(async (err) => {
        console.warn('Device plugin failed, falling back to local storage or generated ID.', err);
        let device = await getDeviceFromLocalStorage();
        if (!device?.id) {
            const id = generateFallbackId();
            const friendlyName = getFriendlyNameFromUserAgent() || 'Unknown Device';
            device = { id, friendlyName };
            saveDeviceToLocalStorage(device);
        }
        deviceSubject.next(device);
    });

const generateFallbackId = () => {
    const stored = localStorage.getItem('fallback_device_id');
    if (stored) return stored;
    const id = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
    localStorage.setItem('fallback_device_id', id);
    return id;
};

const saveDeviceToLocalStorage = (device: Device) => {
    Preferences.set({ key: 'device', value: JSON.stringify(device) });
};

const getDeviceFromLocalStorage = () =>
    new Promise<Device>((resolve, reject) => {
        console.log('checking for an existing device in local storage');

        setTimeout(() => {
            // It seems that sometimes localstorage isn't ready
            // on page load, so use setTimeout
            Preferences.get({ key: 'device' })
                .then((res) => {
                    let deviceId: string;
                    if (res.value) {
                        console.log('existing device: ', res.value);
                        deviceId = JSON.parse(res.value)?.id;
                    } else {
                        resolve(null);
                    }

                    const friendlyName = getFriendlyNameFromUserAgent();
                    resolve({ id: deviceId, friendlyName });
                })
                .catch((err) => {
                    console.error('Error getting device id from local storage.', err);
                    resolve(null);
                });
        }, 500);
    });

const getFriendlyNameFromUserAgent = () => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const browser =
        userAgent.indexOf('edge') > -1
            ? 'Edge'
            : userAgent.indexOf('edg') > -1
              ? 'Chromium Based Edge'
              : userAgent.indexOf('opr') > -1 && !!(window as any).opr
                ? 'Opera'
                : userAgent.indexOf('chrome') > -1 && !!(window as any).chrome
                  ? 'Chrome'
                  : userAgent.indexOf('trident') > -1
                    ? 'Internet Explorer'
                    : userAgent.indexOf('firefox') > -1
                      ? 'Firefox'
                      : userAgent.indexOf('safari') > -1
                        ? 'Safari'
                        : 'Unknown web browser';

    console.log(`Browser: ${userAgent.toLowerCase()}\n${browser}`);
    return browser;
};
